import { Component, Prop, Vue} from "vue-property-decorator";
import { CurvedWallSectionModuleModel, CurvedWallModel, CurvedWallSection, ValueDescription, ConfiguratorValueUpdateModel, CurvedWallSecionModuleOption, WallTypes, CurvedWallSectionDirection, WallSection, Section } from "../models";
import { productConfiguratorStore as store} from "./../store";
import { ValidationProvider, extend } from "vee-validate";
import { between, min_value } from "vee-validate/dist/rules";
import HtmlElementHelper from "@/services/HtmlElementHelper";
import Cookie from "@/services/Cookie";

extend("between", {
    ...between,
    // message: store.translations.notWithinValidIntervalMessage
});

extend("min_value", {
    ...min_value,
    message: store.translations.belowAllowedLimitMessage
});

@Component(
    {
        components: {
            ValidationProvider
          }
    }
)
export default class CurvedWallSectionModule extends Vue {
    @Prop()
    protected vm!: CurvedWallSectionModuleModel;
    protected selectedCustomConfiguratorProductCookie: string = "";

    protected curvedWallModel: CurvedWallModel = { wallSections: [{ selectedOption: { applicableProperies: {}} }]} as CurvedWallModel;
    protected activeSectionTypeSelector: number = -1;
    protected showSectionTypeSelector: boolean = false;

    protected created() {
        if (!this.curvedWallModel.wallSections.length) {
            this.addSection();
        }
        this.getCookie();
    }

    protected getCookie() {
        this.selectedCustomConfiguratorProductCookie = Cookie.getCookie("SelectedCustomConfiguratorProducts-" + this.vm.configuratorType);
        Cookie.applyCustomCookies(this.vm, this.selectedCustomConfiguratorProductCookie, this.curvedWallModel);
        if (this.selectedCustomConfiguratorProductCookie !== "") {
            this.tryUpdateConfiguratorValue();
        }
    }
    protected addSection() {
        const tempSections = this.curvedWallModel.wallSections;
        tempSections.push({ selectedOption: { applicableProperies: {}} } as CurvedWallSection);
        this.curvedWallModel.wallSections = tempSections;
    }

    protected deleteSection(sectionIndex: number) {
        const tempSections = this.curvedWallModel.wallSections;
        tempSections.splice(sectionIndex, 1);
        this.curvedWallModel.wallSections = tempSections;
        if (!this.curvedWallModel.wallSections.length) {
            this.addSection();
        }
        this.tryUpdateConfiguratorValue();
    }

    protected toggleSectionTypeSelector(sectionIndex: number) {
        if (this.activeSectionTypeSelector === sectionIndex) {
            this.showSectionTypeSelector = !this.showSectionTypeSelector;
        } else {
            this.showSectionTypeSelector = true;
            this.activeSectionTypeSelector = sectionIndex;
        }
    }

    protected setSectionOptions(section: CurvedWallSection, option: CurvedWallSecionModuleOption) {
        section.sectionType = option.sectionType;
        section.angle = option.sectionAngle;

        this.resetSectionValues(section);
        this.tryUpdateConfiguratorValue();
    }

    protected tryUpdateConfiguratorValue() {
        if (this.curvedWallModel.wallSections
            .every(s => (s.selectedOption.id &&
            !s.selectedOption.applicableProperies.height || s.heightAboveGround > 0)
            && (!s.selectedOption.applicableProperies.length ||  s.length > 0)
            && (!s.selectedOption.applicableProperies.direction || (s.direction !== CurvedWallSectionDirection.Straight))
            && (!s.selectedOption.applicableProperies.coping || typeof s.coping !== "undefined"))) {
            this.updateConfiguratorValue();
        }
    }

    protected handleInputDebounce(inputValue: any, event: Event) {
        if (HtmlElementHelper.isEventTargetFocusedElement(event.target)) {
            this.tryUpdateConfiguratorValue();
        }
    }

    protected handleSectionHeightUpdate(event: Event, section: WallSection) {
        if (event.target instanceof HTMLInputElement) {
            if (HtmlElementHelper.isValidNumberInput(event.target)) {
                section.heightAboveGround = Number(event.target.value);
            } else if (section.heightAboveGround > 0) {
                section.heightAboveGround = 0;
            }
        }

        this.tryUpdateConfiguratorValue();
    }

    private resetSectionValues(section: CurvedWallSection) {
        if (!section.selectedOption) {
            return;
        }

        if (!section.selectedOption.applicableProperies.height) {
            section.heightAboveGround = 0;
        }

        if (!section.selectedOption.applicableProperies.length) {
            section.length = 0;
        }

        if (!section.selectedOption.applicableProperies.direction) {
            section.direction = 0;
        }

        if (!section.selectedOption.applicableProperies.coping) {
            section.coping = undefined;
        } else if (section.coping === undefined) {
            section.coping = true;
        }
    }

    private updateConfiguratorValue() {
        const valueDescriptions: ValueDescription[] = this.curvedWallModel.wallSections.map(section => (
            { title: section.selectedOption.description, text: `${ this.getValueDescription(section)}`
        }));

        const updateModel: ConfiguratorValueUpdateModel = { moduleId: this.vm.id, value: this.curvedWallModel, valueDescriptions,
        propertyName: this.vm.configuratorProperties[0].propertyName, isText: false, resetClimateSavingsCookie: false };
        this.$emit("update-configurator-value", updateModel, true);
    }

    private getValueDescription(wallSection: CurvedWallSection): string {
        let description: string = "";

        if (wallSection.selectedOption.applicableProperies.direction === true) {
            let directionDescription: string = "";

            switch (wallSection.direction) {
                case CurvedWallSectionDirection.Left:
                    directionDescription = store.translations.toTheLeft;
                    break;
                case CurvedWallSectionDirection.Right:
                    directionDescription = store.translations.toTheRight;
                    break;
            }

            description += `${directionDescription}`;
        }

        if (wallSection.selectedOption.applicableProperies.length === true) {
            if (description) {
                description += ", ";
            }
            description += `${store.translations.commonLength}: ${wallSection.length} m`;
        }

        if (wallSection.selectedOption.applicableProperies.height === true) {
            if (description) {
                description += ", ";
            }
            description += `${store.translations.commonHeight}: ${wallSection.heightAboveGround} m`;
        }

        if (wallSection.selectedOption.applicableProperies.height === true && wallSection.coping === true) {
            if (description) {
                description += ", ";
            }
            description += `${store.translations.coping}`;
        }

        return description;
    }

    get getSectionMaxHeight() {
        if (this.isRetainingWall) {
            return 1.5;
        }

        return 2;
    }

    get isRetainingWall() {
        return store.configuratorValues.some(cv => cv.property.propertyName === "WallType" && cv.value ===  WallTypes.Retaining.toString());
    }

    get translations() {
        return store.translations;
    }
}
